import { useState, useRef } from "react";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  User,
  Mail,
  Building2,
  Briefcase,
  Save,
  Loader2,
  Phone,
  CalendarDays,
  MapPin,
  Camera,
} from "lucide-react";

// Redimensionne une image et retourne un Blob JPEG
const resizeToBlob = (dataUrl, maxW, maxH) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(maxW / img.width, maxH / img.height, 1);
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))),
        "image/jpeg",
        0.82
      );
    };
    img.onerror = () => reject(new Error("Impossible de lire l'image"));
    img.src = dataUrl;
  });

const ProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Form state
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [department, setDepartment] = useState(user?.department || "");
  const [position, setPosition] = useState(user?.position || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [birthDate, setBirthDate] = useState(user?.birth_date || "");
  const [address, setAddress] = useState(user?.address || "");

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  const getRoleBadge = (role) => {
    const config = {
      admin: { label: "Administrateur", className: "bg-purple-600 text-white" },
      hr: { label: "Ressources Humaines", className: "bg-[#E07A5F] text-white" },
      manager: { label: "Manager", className: "bg-[#1A4D2E] text-white" },
      employee: { label: "Employé", className: "bg-[#E8F5E9] text-[#1A4D2E]" },
      collaborateur: { label: "Collaborateur", className: "bg-[#E8F5E9] text-[#1A4D2E]" }
    };
    const { label, className } = config[role] || config.collaborateur;
    return <Badge className={`${className} text-sm`}>{label}</Badge>;
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner une image");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5 Mo");
      return;
    }

    setAvatarUploading(true);
    try {
      const raw = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const blob = await resizeToBlob(raw, 256, 256);
      const formData = new FormData();
      formData.append("file", blob, "avatar.jpg");
      await axios.put(`${API}/users/${user.id}/avatar`, formData);
      toast.success("Photo mise à jour !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'upload de la photo");
    } finally {
      setAvatarUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      refreshUser?.();
    }
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      await axios.put(`${API}/users/${user.id}`, {
        first_name: firstName,
        last_name: lastName,
        department,
        position,
        phone,
        birth_date: birthDate || null,
        address
      });
      toast.success("Profil mis à jour avec succès !");
      setIsEditing(false);
      refreshUser?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFirstName(user?.first_name || "");
    setLastName(user?.last_name || "");
    setDepartment(user?.department || "");
    setPosition(user?.position || "");
    setPhone(user?.phone || "");
    setBirthDate(user?.birth_date || "");
    setAddress(user?.address || "");
    setIsEditing(false);
  };

  const readonlyClass = "py-2 px-3 bg-gray-50 dark:bg-[#112240] rounded-lg text-[#111827] dark:text-[#D6E4F5]";

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className="font-['Playfair_Display'] text-xl md:text-3xl font-bold text-[#111827] dark:text-[#D6E4F5]">
          Mon profil
        </h1>
        <p className="text-[#4B5563] dark:text-[#7EA8CE] mt-1 text-sm md:text-base">
          Gérez vos informations personnelles
        </p>
      </div>

      {/* Profile Card */}
      <Card className="card-neumorphic overflow-hidden">
        {/* Bannière verte */}
        <div className="h-32 bg-gradient-to-br from-[#1A4D2E] to-[#143D24] dark:from-[#1A3A6A] dark:to-[#0D1E3A]" />

        <div className="px-6 pb-6">
          {/* Avatar + Badge sur la même ligne */}
          <div className="flex justify-between items-start -mt-12 mb-4">
            {/* Avatar cliquable */}
            <div className="relative group">
              <Avatar className="w-24 h-24 border-4 border-white dark:border-[#0B1628] shadow-lg">
                <AvatarImage src={user?.avatar_url} />
                <AvatarFallback className="bg-[#E8F5E9] dark:bg-[#1A3A6A] text-[#1A4D2E] dark:text-[#4B8BF4] text-2xl font-bold">
                  {getInitials(user?.first_name, user?.last_name)}
                </AvatarFallback>
              </Avatar>

              {/* Overlay caméra au hover */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={avatarUploading}
                title="Changer la photo"
                className="absolute inset-0 rounded-full bg-black/45 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                {avatarUploading ? (
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                ) : (
                  <Camera className="w-6 h-6 text-white" />
                )}
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarChange}
              />
            </div>

            {/* Badge — décalé vers le bas pour ne pas chevaucher la bannière */}
            <div className="mt-14">
              {getRoleBadge(user?.role)}
            </div>
          </div>

          {/* Nom & email — clairement sous la bannière verte */}
          <div>
            <h2 className="text-2xl font-bold text-[#111827] dark:text-[#D6E4F5]">
              {user?.first_name} {user?.last_name}
            </h2>
            <p className="text-[#4B5563] dark:text-[#7EA8CE] text-sm mt-0.5">
              {user?.email}
            </p>
          </div>
        </div>
      </Card>

      {/* Edit Form */}
      <Card className="card-neumorphic">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-['Playfair_Display'] text-xl dark:text-[#D6E4F5]">
            Informations personnelles
          </CardTitle>
          {!isEditing ? (
            <Button
              variant="outline"
              onClick={() => setIsEditing(true)}
              className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#E8F5E9] dark:border-[#1A3050] dark:text-[#4B8BF4] dark:hover:bg-[#112240]"
              data-testid="edit-profile-btn"
            >
              Modifier
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleCancel}
                className="dark:border-[#1A3050] dark:text-[#D6E4F5] dark:hover:bg-[#112240]"
              >
                Annuler
              </Button>
              <Button
                onClick={handleSave}
                className="bg-[#1A4D2E] hover:bg-[#143D24] dark:bg-[#166534] dark:hover:bg-[#14532d]"
                disabled={isSubmitting}
                data-testid="save-profile-btn"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Prénom */}
            <div className="space-y-2">
              <Label htmlFor="firstName" className="flex items-center gap-2 dark:text-[#D6E4F5]">
                <User className="w-4 h-4 text-[#1A4D2E] dark:text-[#4B8BF4]" />
                Prénom
              </Label>
              {isEditing ? (
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                  data-testid="profile-firstname"
                />
              ) : (
                <p className={readonlyClass}>{user?.first_name}</p>
              )}
            </div>

            {/* Nom */}
            <div className="space-y-2">
              <Label htmlFor="lastName" className="flex items-center gap-2 dark:text-[#D6E4F5]">
                <User className="w-4 h-4 text-[#1A4D2E] dark:text-[#4B8BF4]" />
                Nom
              </Label>
              {isEditing ? (
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                  data-testid="profile-lastname"
                />
              ) : (
                <p className={readonlyClass}>{user?.last_name}</p>
              )}
            </div>

            {/* Email — toujours lecture seule */}
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2 dark:text-[#D6E4F5]">
                <Mail className="w-4 h-4 text-[#1A4D2E] dark:text-[#4B8BF4]" />
                Email
              </Label>
              <p className="py-2 px-3 bg-gray-50 dark:bg-[#112240] rounded-lg text-[#4B5563] dark:text-[#7EA8CE]">
                {user?.email}
              </p>
            </div>

            {/* Téléphone */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2 dark:text-[#D6E4F5]">
                <Phone className="w-4 h-4 text-[#1A4D2E] dark:text-[#4B8BF4]" />
                Téléphone
              </Label>
              {isEditing ? (
                <Input
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Ex: +33 6 12 34 56 78"
                  className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                  data-testid="profile-phone"
                />
              ) : (
                <p className={readonlyClass}>{user?.phone || "-"}</p>
              )}
            </div>

            {/* Département */}
            <div className="space-y-2">
              <Label htmlFor="department" className="flex items-center gap-2 dark:text-[#D6E4F5]">
                <Building2 className="w-4 h-4 text-[#1A4D2E] dark:text-[#4B8BF4]" />
                Département
              </Label>
              {isEditing ? (
                <Select value={department} onValueChange={setDepartment}>
                  <SelectTrigger data-testid="profile-department" className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]">
                    <SelectValue placeholder="Sélectionnez" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Expert métier">Expert métier</SelectItem>
                    <SelectItem value="Ressources Humaines">Ressources Humaines</SelectItem>
                    <SelectItem value="Direction">Direction</SelectItem>
                    <SelectItem value="Technique">Technique</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className={readonlyClass}>{user?.department || "-"}</p>
              )}
            </div>

            {/* Date de naissance */}
            <div className="space-y-2">
              <Label htmlFor="birthDate" className="flex items-center gap-2 dark:text-[#D6E4F5]">
                <CalendarDays className="w-4 h-4 text-[#1A4D2E] dark:text-[#4B8BF4]" />
                Date de naissance
              </Label>
              {isEditing ? (
                <Input
                  id="birthDate"
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                  data-testid="profile-birthdate"
                />
              ) : (
                <p className={readonlyClass}>{user?.birth_date || "-"}</p>
              )}
            </div>

            {/* Poste */}
            <div className="space-y-2">
              <Label htmlFor="position" className="flex items-center gap-2 dark:text-[#D6E4F5]">
                <Briefcase className="w-4 h-4 text-[#1A4D2E] dark:text-[#4B8BF4]" />
                Poste
              </Label>
              {isEditing ? (
                <Input
                  id="position"
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  placeholder="Ex: Développeur Full Stack"
                  className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                  data-testid="profile-position"
                />
              ) : (
                <p className={readonlyClass}>{user?.position || "-"}</p>
              )}
            </div>

            {/* Date d'embauche — lecture seule */}
            <div className="space-y-2">
              <Label htmlFor="hireDate" className="flex items-center gap-2 dark:text-[#D6E4F5]">
                <Building2 className="w-4 h-4 text-[#1A4D2E] dark:text-[#4B8BF4]" />
                Date d'embauche
              </Label>
              <p className="py-2 px-3 bg-gray-50 dark:bg-[#112240] rounded-lg text-[#4B5563] dark:text-[#7EA8CE]">
                {user?.hire_date || "-"}
              </p>
            </div>

            {/* Adresse */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="address" className="flex items-center gap-2 dark:text-[#D6E4F5]">
                <MapPin className="w-4 h-4 text-[#1A4D2E] dark:text-[#4B8BF4]" />
                Adresse
              </Label>
              {isEditing ? (
                <Input
                  id="address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Ex: 12 rue de la Paix, 75001 Paris"
                  className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                  data-testid="profile-address"
                />
              ) : (
                <p className={readonlyClass}>{user?.address || "-"}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePage;
